import { ReactNode, useState, useCallback } from "react";
import { Snackbar, Alert } from "@mui/material";
import { ToastContext } from "./ToastContext";
import type { ToastSeverity } from "./ToastContext";

/**
 * ToastProvider - Centralized toast notification system
 *
 * This provider manages all toast notifications in the application.
 * IMPORTANT: Do not use MUI's Snackbar component directly in other components.
 * Always use the useToast hook to show toasts.
 *
 * This ensures:
 * - Consistent toast styling across the app
 * - Single source of truth for toast behavior
 * - Easier maintenance and updates
 */

interface ToastProviderProps {
  children: ReactNode;
}

interface ToastState {
  open: boolean;
  message: string;
  severity: ToastSeverity;
}

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: "",
    severity: "info",
  });

  const showToast = useCallback(
    (message: string, severity: ToastSeverity = "info") => {
      setToast({
        open: true,
        message,
        severity,
      });
    },
    [],
  );

  const showSuccess = useCallback(
    (message: string) => showToast(message, "success"),
    [showToast],
  );

  const showError = useCallback(
    (message: string, error?: unknown) => {
      console.error(message, error);
      showToast(message, "error");
    },
    [showToast],
  );

  const showInfo = useCallback(
    (message: string) => showToast(message, "info"),
    [showToast],
  );

  const showWarning = useCallback(
    (message: string) => showToast(message, "warning"),
    [showToast],
  );

  const handleClose = useCallback(() => {
    setToast((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <ToastContext.Provider
      value={{
        showToast,
        showSuccess,
        showError,
        showInfo,
        showWarning,
      }}
    >
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleClose}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};
